import React from "react";

/**
 * Faint screenplay-format pattern for the hero background.
 *
 * Stylized scene headings, action lines, and dialogue rendered at ~10% opacity
 * over the deep charcoal hero background. The rendering uses the actual structural
 * shape of a screenplay page — fixed-width type, centered character cues over
 * indented dialogue, parentheticals, and slug lines — without ever being legible
 * enough to read as text. The bones of the format the product analyzes.
 *
 * Implemented as inline SVG so it scales perfectly, ships in <2KB, and never
 * triggers an extra HTTP request.
 */
export const ScreenplayPattern = ({ className = "" }) => {
  // A single screenplay "tile" we repeat vertically. Designed at 600 × 760.
  return (
    <svg
      viewBox="0 0 600 760"
      preserveAspectRatio="xMidYMin slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
      data-testid="screenplay-pattern"
    >
      <defs>
        {/* Tile pattern repeats vertically the same screenplay block */}
        <pattern id="vs-screenplay-tile" x="0" y="0" width="600" height="760" patternUnits="userSpaceOnUse">
          <g
            fontFamily="'Courier Prime', 'Source Code Pro', 'Inter', monospace"
            fontSize="13"
            fill="#E8E4D6"
            opacity="0.12"
          >
            {/* SLUG LINE — scene heading */}
            <text x="60" y="40" fontWeight="600" letterSpacing="0.06em">INT. WRITER'S ROOM — NIGHT</text>

            {/* Action paragraph */}
            <text x="60" y="78">A single lamp burns on a desk crowded with index cards.</text>
            <text x="60" y="96">Pages stacked. A pen, uncapped. Someone reads, again, the</text>
            <text x="60" y="114">opening of a script they've been working on for months.</text>

            {/* Character cue (centered, uppercase) */}
            <text x="270" y="158" fontWeight="600" letterSpacing="0.04em">THE READER</text>
            <text x="230" y="176" fontStyle="italic" opacity="0.85">(without flattery)</text>

            {/* Dialogue — narrower margins */}
            <text x="180" y="200">The premise holds. The protagonist doesn't yet.</text>
            <text x="180" y="218">You're asking us to follow a person whose want we</text>
            <text x="180" y="236">never quite see articulated on the page. Fix the</text>
            <text x="180" y="254">want, and the rest of the structure can answer.</text>

            {/* Slug line */}
            <text x="60" y="304" fontWeight="600" letterSpacing="0.06em">INT. PRODUCER'S OFFICE — DAY</text>

            {/* Action */}
            <text x="60" y="342">The script is on the desk. Margins are crowded with notes.</text>
            <text x="60" y="360">A second copy lies beside it, untouched, the cover page</text>
            <text x="60" y="378">slightly creased where it has been read too many times.</text>

            {/* Character cue */}
            <text x="270" y="422" fontWeight="600" letterSpacing="0.04em">THE PRODUCER</text>

            <text x="180" y="446">Three acts that work. A second act that drags by</text>
            <text x="180" y="464">eight pages. A climax I believed before the setup</text>
            <text x="180" y="482">earned it. I'd recommend a rewrite — not a pass.</text>

            {/* Slug */}
            <text x="60" y="530" fontWeight="600" letterSpacing="0.06em">EXT. THE GREEN-LIT PROJECT — CONTINUOUS</text>

            {/* Action */}
            <text x="60" y="568">The page turns, finally, on its own weight.</text>
            <text x="60" y="586">The work is ready. The work is ready. The work is ready.</text>

            {/* Character cue */}
            <text x="270" y="630" fontWeight="600" letterSpacing="0.04em">THE COUNCIL</text>
            <text x="240" y="648" fontStyle="italic" opacity="0.85">(in unison)</text>

            <text x="180" y="672">Recommend.</text>

            {/* Page number */}
            <text x="540" y="730" fontFamily="'Inter', sans-serif" fontSize="11" opacity="0.6">17.</text>
          </g>

          {/* Tiny accent ornament — a single gold vertical scene divider */}
          <line x1="40" y1="20" x2="40" y2="740" stroke="#C9A961" strokeWidth="0.5" opacity="0.18" />
        </pattern>

        {/* Vignette: fade pattern toward center where the hero card sits.
            Softened (was 0.85/0.50) so the screenplay pattern remains
            perceptible behind the card edges without competing with it. */}
        <radialGradient id="vs-vignette" cx="50%" cy="50%" r="68%">
          <stop offset="0%"   stopColor="#161117" stopOpacity="0.70" />
          <stop offset="55%"  stopColor="#161117" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#161117" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="100%" height="100%" fill="url(#vs-screenplay-tile)" />
      {/* Vignette to keep the hero card area visually clean */}
      <rect width="100%" height="100%" fill="url(#vs-vignette)" />
    </svg>
  );
};

export default ScreenplayPattern;
